import { Dices, Trophy, BookOpen, Heart, Lightbulb } from "lucide-react";
import type { ArticleContent, ArticleTile } from "./types";
import { Hero, Section, Steps, Bullets, Tip } from "./renderHelpers";

const CATEGORY = "Fundraising";

export const tiles: ArticleTile[] = [
  { slug: "guessing-games", title: "Running the Teddy Name & Cake Weight Games", category: CATEGORY, description: "Classic fair games run digitally", icon: Dices },
  { slug: "bingo-game", title: "Setting Up the Bingo Game", category: CATEGORY, description: "Host bingo nights with auto-generated cards", icon: Trophy },
  { slug: "fundraising-library", title: "Using the Fundraising Library", category: CATEGORY, description: "Browse curated fundraising ideas", icon: BookOpen },
  { slug: "sponsorship-packages", title: "Sponsorship Packages & Public Checkout", category: CATEGORY, description: "Sell sponsorship to local businesses", icon: Heart },
];

export const articles: Record<string, ArticleContent> = {
  "guessing-games": {
    title: "Running the Teddy Name & Cake Weight Games",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Two classic PTA fair games — Guess the Teddy's Name and Guess the Cake Weight — run digitally with an online cart for parents.</Hero>
        <Section icon={Dices} title="Set up a game">
          <Steps items={[
            <>Open <strong>Games</strong> from the sidebar.</>,
            <>Click <strong>Create Game</strong> and pick the type.</>,
            <>Set entry price, max entries per person and end date.</>,
            <>Share the public game link with parents.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Pick a winner">
          <Bullets items={[
            "Enter the actual teddy name or cake weight after the event.",
            "PTA Hub picks the winning entry automatically.",
            "Email the winner directly from the game page.",
          ]} />
        </Section>
      </div>
    ),
  },
  "bingo-game": {
    title: "Setting Up the Bingo Game",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Run a bingo evening with auto-generated cards for every player and a built-in number caller.</Hero>
        <Section icon={Trophy} title="Start a bingo session">
          <Steps items={[
            <>Open <strong>Games → Bingo</strong>.</>,
            <>Click <strong>New Session</strong> and set the ticket price.</>,
            <>Share the link — players join and get a unique card.</>,
            <>Use the caller controls on stage to draw numbers.</>,
          ]} />
        </Section>
      </div>
    ),
  },
  "fundraising-library": {
    title: "Using the Fundraising Library",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>The Fundraising Library is a curated collection of proven fundraising ideas, complete with how-to guides and printable assets.</Hero>
        <Section icon={BookOpen} title="Browse and save">
          <Steps items={[
            <>Open <strong>Library</strong> from the sidebar.</>,
            <>Filter by category, season or effort level.</>,
            <>Save ideas to your shortlist for later.</>,
          ]} />
        </Section>
        <Tip>Members can submit their own photos (max 3) — admins curate the cover image.</Tip>
      </div>
    ),
  },
  "sponsorship-packages": {
    title: "Sponsorship Packages & Public Checkout",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Sell sponsorship packages (e.g. "Gold Sponsor — £250") through a public Mollie checkout. Sponsors are listed on your fundraising page.</Hero>
        <Section icon={Heart} title="Create a sponsorship tier">
          <Steps items={[
            <>Open <strong>Fundraising → Sponsors</strong>.</>,
            <>Click <strong>Add Sponsorship Tier</strong> and set name, price and benefits.</>,
            <>Publish — businesses can purchase directly from your fundraising page.</>,
          ]} />
        </Section>
        <Tip>Sponsors receive a confirmation email with a PDF invoice automatically.</Tip>
      </div>
    ),
  },
};
